// ============================================================================
// TrimaProgressHeader — Top progress block for every Trima step screen
// ============================================================================
// All three counter elements are togglable via props so the design can evolve
// without changing the call sites.
//
// Layout (when all enabled):
//   Étape 2 / 5                            Cadran 1 / 4
//   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ============================================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrimaProgressHeaderWrapper } from 'pages/questionnaire-trima/components/TrimaProgressHeader.styled';
import { TRIMA_TOTAL_STEPS } from 'pages/questionnaire-trima/constants/trima.constants';

export interface TrimaProgressHeaderProps {
  /**
   * Current global step (1..5). 1 = ranking, 2..5 = cadrans 1..4.
   */
  currentStep: number;
  /**
   * Overall progress 0..100. Computed by the parent so it can mix
   * ranking-question-level and cadran-level granularity.
   */
  progressPercent: number;
  /**
   * Optional local counter, e.g. "Question 4 / 24" or "Cadran 1 / 4".
   * Pre-formatted by the caller for flexibility.
   */
  localCounterLabel?: string;
  /** Hide the "Étape X / 5" line. */
  hideGlobalStep?: boolean;
  /** Hide the thin progress bar. */
  hideProgressBar?: boolean;
  /** Hide the localCounterLabel line. */
  hideLocalCounter?: boolean;
}

const TrimaProgressHeader: React.FC<TrimaProgressHeaderProps> = ({
  currentStep,
  progressPercent,
  localCounterLabel,
  hideGlobalStep,
  hideProgressBar,
  hideLocalCounter,
}) => {
  const { t } = useTranslation('trima');
  const safeProgress = Math.max(0, Math.min(100, progressPercent));

  const showRow = !hideGlobalStep || (!hideLocalCounter && localCounterLabel);

  return (
    <TrimaProgressHeaderWrapper>
      {showRow && (
        <div className="trima-step-row">
          {!hideGlobalStep && (
            <span className="trima-step-global">
              {t('ranking.step-label', { current: currentStep, total: TRIMA_TOTAL_STEPS })}
            </span>
          )}
          {!hideLocalCounter && localCounterLabel && (
            <span className="trima-step-local">{localCounterLabel}</span>
          )}
        </div>
      )}
      {!hideProgressBar && (
        <div className="trima-progress-bar">
          <div className="trima-progress-bar-fill" style={{ width: `${safeProgress}%` }} />
        </div>
      )}
    </TrimaProgressHeaderWrapper>
  );
};

export default TrimaProgressHeader;
